import { useDealFormContext } from '@openx/components-deal/lib/DealForm/DealForm.context';
import type { UseSetMultiproviderEnabledDataPartner } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { type DealFormData, type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import type { TargetingState } from '@openx/types/targeting/targeting';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useGetAudienceMultiproviderInfo } from '../../targeting/utils/useGetAudienceMultiproviderInfo';

export const useSetMultiproviderEnabledDataPartner: UseSetMultiproviderEnabledDataPartner = () => {
  const { setValue, trigger, getFieldState } = useFormContext<DealFormData & { targeting: TargetingState }>();
  const { pckg } = useDealFormContext();

  const observableAudienceObj = useWatch<DealFormDataWithTargeting, 'targeting.targeting.audience.openaudience_custom'>(
    {
      name: 'targeting.targeting.audience.openaudience_custom',
    }
  );

  const selectedAudience = useMemo(() => {
    if (pckg?.uid && pckg?.targeting?.audience?.openaudience_custom?.val) {
      return pckg.targeting.audience.openaudience_custom.val;
    }

    if (observableAudienceObj) {
      return observableAudienceObj.val;
    }

    return '';
  }, [observableAudienceObj, pckg?.uid, pckg?.targeting?.audience]);

  const { hasMultiProviderPricingData, pricingInfo, loadingAudienceCheck } = useGetAudienceMultiproviderInfo({
    selectedAudienceOption: 'openaudience_custom',
    values: [selectedAudience ?? ''],
  });

  useEffect(() => {
    if (!selectedAudience) {
      setValue(dealFormField.HAS_MULTIPROVIDER_AUDIENCE, false, { shouldDirty: true });
      setValue(dealFormField.PRICING_BY_ORG_DATA, null, { shouldDirty: false });
    } else if (!loadingAudienceCheck) {
      setValue(dealFormField.HAS_MULTIPROVIDER_AUDIENCE, hasMultiProviderPricingData, { shouldDirty: true });
      setValue(dealFormField.PRICING_BY_ORG_DATA, pricingInfo?.pricing_by_org || null, { shouldDirty: false });
    }

    // re-check a 94% error raised against the old pricing; the fees section's effects ran before this with it
    if (getFieldState('third_party_fees_config.0.gross_share').error) {
      trigger('third_party_fees_config.0.gross_share');
    }
  }, [
    hasMultiProviderPricingData,
    selectedAudience,
    setValue,
    loadingAudienceCheck,
    pricingInfo?.pricing_by_org,
    getFieldState,
    trigger,
  ]);

  return { hasMultiProviderPricingData, pricingInfo, loadingAudienceCheck };
};
